import type { AuthTokens, LoginRequest, User } from '@/types';
import { api } from './client';

export interface AuthResponse extends AuthTokens {
  user: User;
}

/** The password was right; a code is needed, or two-factor must be set up first. */
export interface SignInChallenge {
  mfaRequired: 'verify' | 'setup';
  mfaToken: string;
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
  setupToken: string;
}

interface RefreshResponse extends AuthTokens {
  user: User;
}

interface SessionInfo {
  id: string;
  createdAt: string;
  expiresAt: string;
  userAgent?: string;
  ipAddress?: string;
  isCurrent: boolean;
}

export const authService = {
  login: (data: LoginRequest): Promise<AuthResponse | SignInChallenge> =>
    api.post<AuthResponse | SignInChallenge>('/auth/login', data),

  verifyMfa: (mfaToken: string, code: string): Promise<AuthResponse> =>
    api.post<AuthResponse>('/auth/mfa/verify', { mfaToken, code }),

  startMfaSetup: (mfaToken: string): Promise<TwoFactorSetup> =>
    api.post<TwoFactorSetup>('/auth/mfa/setup', { mfaToken }),

  confirmMfaSetup: (
    mfaToken: string,
    setupToken: string,
    code: string,
  ): Promise<AuthResponse & { recoveryCodes: string[] }> =>
    api.post('/auth/mfa/setup/confirm', { mfaToken, setupToken, code }),

  exchangeSso: (code: string): Promise<AuthResponse> => api.post<AuthResponse>('/auth/sso/exchange', { code }),

  logout: (): Promise<void> =>
    api.post('/auth/logout'),

  refreshToken: (): Promise<RefreshResponse> =>
    api.post<RefreshResponse>('/auth/refresh'),

  getCurrentUser: (): Promise<User> => api.get<User>('/auth/me'),

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => api.post('/auth/change-password', data),

  forgotPassword: (email: string): Promise<void> =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (data: { email: string; otp: string; password: string }): Promise<void> =>
    api.post('/auth/reset-password', data),

  forceResetPassword: (newPassword: string): Promise<void> =>
    api.post('/auth/force-reset-password', { newPassword }),

  revokeSession: (sessionId: string): Promise<void> =>
    api.delete(`/auth/sessions/${sessionId}`),

  revokeAllSessions: (): Promise<void> =>
    api.post('/auth/sessions/revoke-all'),

  getSessions: (): Promise<SessionInfo[]> =>
    api.get<SessionInfo[]>('/auth/sessions'),
};
